/* eslint-disable @typescript-eslint/no-explicit-any */
import {DestroyRef, Inject, Injectable, DOCUMENT} from '@angular/core';
import {EsriModuleService} from '../../../../esri-nlr/services/esri-module.service';
import {MapToolsService} from '../../../../services/map-tools.service';
import {LineService} from '../../../../services/features/line.service';
import {MapService} from '../../../../services/map.service';
import {LookupService} from '../../../../../common/service/lookup.service';
import {SpatialSequenceService} from '../../../../../common/service/spatial-sequence.service';
import {SpatialUtilService} from '../../../../services/spatial-util.service';
import {LayerUtilService} from '../../../../layers/layer-util.service';
import {ConstructionToolModelService} from './construction-tool-model.service';
import {ConstructionUtilService} from './construction-util.service';

import {map, of} from 'rxjs';
import {switchMap} from 'rxjs/operators';
import {ddToDms} from 'src/app/design/services/features/angle/angle-tools';
import {LineTypeLookup} from '../../../../../common/models/line-type.model';
import {NgEventBus} from 'ng-event-bus';
import {PointTypeLookup} from '../../../../../common/models/point-type.model';
import {FeaturesApplyInsertService} from '../../../../services/features-apply-insert.service';
import {forkJoinDefaultIfEmpty} from '../../../../../common/observable.utils';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';

@Injectable({
    providedIn: 'root'
})
export class LineConstructionToolService {
    title = 'LineConstructionToolService';

    attributes: any;

    private inCreateLine = false;

    private selectedPoint1: any;

    private selectedPoint2: any;

    private lastClickedPoint: any;

    private mapClickEvent: any;

    private mapDblClickEvent: any;

    private mapUnloadEvent: any;

    private mapMouseMoveEvent: any;

    private mapMouseOverEvent: any;

    private mapZoomEndEvent: any;

    private lineTypeLookup: LineTypeLookup;

    private readonly lineStyleNodeId = this.layerUtilService.getFeatureNodeId('line', 0);

    private readonly lineNodeAddEvent: {
        graphic: {attributes: {id: number; type: number}};
        node: SVGElement;
        endMapX?: number;
        endMapY?: number;
        startMapX?: number;
        startMapY?: number;
        startScreenX?: number;
        startScreenY?: number;
    } = {
        graphic: {attributes: {id: 0, type: 0}},
        node: null
    };

    constructor(
        @Inject(DOCUMENT) private document: Document,
        private constructionToolModelService: ConstructionToolModelService,
        private constructionUtilService: ConstructionUtilService,
        private esriModuleService: EsriModuleService,
        private eventBus: NgEventBus,
        private featuresApplyInsertService: FeaturesApplyInsertService,
        private layerUtilService: LayerUtilService,
        private lineService: LineService,
        private lookupService: LookupService,
        private mapService: MapService,
        private mapToolsService: MapToolsService,
        private spatialSequenceService: SpatialSequenceService,
        private spatialUtilService: SpatialUtilService,
        private destroyRef: DestroyRef
    ) {}

    setupFeatureEventModification(options: {attributes: any}) {
        console.debug(`${this.title} - setupFeatureEventModification`);

        this.attributes = options.attributes;

        this.eventBus
            .on('constructionToolService:line:clear')
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                console.debug('ClearConstructionTool');
                this.mapToolsService.deactivate('CONSTRUCTION_TOOLS_LINE');
                this.clear();
            });

        this.eventBus
            .on('constructionTool:line:close')
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                console.debug('CloseConstructionTool');
                this.clear();
                this.mapUnloadHandler();
            });
    }

    create(attributes: any) {
        console.debug(`${this.title} - create`);
        return this.mapToolsService.activate(
            'CONSTRUCTION_TOOLS_LINE',
            () => {
                this.spatialUtilService.setMapCursor(this.spatialUtilService.getCrossCursorUrl());

                this.attributes = attributes;
                this.mapUnloadHandler();

                if (!this.mapUnloadEvent) {
                    this.mapUnloadEvent = this.mapService.map.on('unload', () => this.mapUnloadHandler());
                }

                this.setupTools();
            },
            () => {
                this.mapUnloadHandler();
                this.eventBus.cast('deactivateConstructionTools');
            }
        );
    }

    finish() {
        console.debug(`${this.title} - finish`);
        this.clear();
        this.eventBus.cast('resetActionBehaviours');
        return of(null);
    }

    onTypeChange(attributes: any) {
        this.attributes = attributes;
    }

    private clear() {
        this.removeSvg();
        if (this.mapMouseMoveEvent) {
            this.mapMouseMoveEvent.remove();
            delete this.mapMouseMoveEvent;
        }
        if (this.mapMouseOverEvent) {
            this.mapMouseOverEvent.remove();
            delete this.mapMouseOverEvent;
        }
        if (this.mapZoomEndEvent) {
            this.mapZoomEndEvent.remove();
            delete this.mapZoomEndEvent;
        }
    }

    private setSnappingActive(snappingActive: boolean): void {
        const mgr: any = this.mapService.snappingManagerTool;
        if (!mgr || typeof mgr._snappingActive !== 'boolean') {
            return;
        }

        mgr._snappingActive = snappingActive;

        const snapPath = this.document.querySelector<HTMLElement>('#mainMap_graphics_layer > path');
        if (snapPath) {
            snapPath.style.display = snappingActive ? 'block' : 'none';
        }
    }

    private setupTools() {
        console.debug(`${this.title} - setupTools`);

        this.selectedPoint1 = undefined;
        this.selectedPoint2 = undefined;
        this.lastClickedPoint = undefined;
        this.inCreateLine = false;

        this.mapService.disableMeasurementTool();
        this.mapService.disableSnappingTool();

        this.lineTypeLookup = this.lookupService.getLookups('lineType').find((l: LineTypeLookup) => l.id === this.attributes.type.id);
        this.enableSnapping();
        this.enableMeasurement();

        this.setupEvents();
    }

    private enableMeasurement() {
        this.mapService.enableMeasurementTool('distance', {
            lineSymbol: new this.esriModuleService.SimpleLineSymbol(
                this.esriModuleService.SimpleLineSymbol.STYLE_NULL,
                new this.esriModuleService.Color([0, 0, 0, 0.0]),
                1
            ),
            pointSymbol: new this.esriModuleService.SimpleMarkerSymbol(
                this.esriModuleService.SimpleMarkerSymbol.STYLE_CROSS,
                1,
                new this.esriModuleService.SimpleLineSymbol(
                    this.esriModuleService.SimpleLineSymbol.STYLE_NULL,
                    new this.esriModuleService.Color([0, 0, 0, 0.0]),
                    1
                ),
                new this.esriModuleService.Color([0, 0, 0, 0.0])
            )
        });
    }

    private enableSnapping() {
        this.mapService.enableSnappingTool(
            [
                this.spatialUtilService.getPointMapLayer(),
                this.spatialUtilService.getLineMapLayer(),
                this.spatialUtilService.getControlPointMapLayer()
            ],
            {
                ctrlKeyDisablesSnapping: this.lineTypeLookup.ctrlKeyDisablesSnapping,
                snapToEdge: true,
                snapToPoint: true,
                tolerance: 30
            }
        );
    }

    private setupEvents() {
        console.debug(`${this.title} - setupEvents`);

        this.mapService.measurementTool.on('measure', () => this.mapMeasureHandler());

        this.clear();

        this.mapMouseMoveEvent = this.mapService.map.on('mouse-move', (evt: any) => {
            if (this.mapService.measurementTool?._currentStartPt) {
                this.updateSvg(evt.layerX, evt.layerY, true, 'mouse-move');
            }
        });

        this.mapMouseOverEvent = this.mapService.map.on('mouse-over', (evt: any) => {
            this.setSnappingActive(!evt.ctrlKey);
        });

        this.mapZoomEndEvent = this.mapService.map.on('zoom-end', (evt: any) => {
            if (this.mapService.measurementTool?._currentStartPt) {
                this.updateSvg(evt.anchor.x, evt.anchor.y, true, 'zoom-end');
            }
        });

        if (!this.mapClickEvent) {
            this.mapClickEvent = this.mapService.map.on('click', (evt: any) => this.mapClickHandler(evt));
        }

        if (!this.mapDblClickEvent) {
            this.mapDblClickEvent = this.mapService.map.on('dbl-click', () => this.mapDblClickHandler());
        }
    }

    private mapClickHandler(evt: {ctrlKey?: any; mapPoint?: any}) {
        const logPrefix = `${this.title} - mapClickHandler`;

        console.debug(logPrefix, evt);

        if (this.mapService.snappingManagerTool) {
            this.enableSnapping();
        }

        const point = this.spatialUtilService.getMap().toScreen(evt.mapPoint);

        this.lastClickedPoint = evt.mapPoint;

        if (this.mapService.snappingManagerTool && !this.inCreateLine) {
            if (evt.ctrlKey && this.lineTypeLookup && this.lineTypeLookup.ctrlKeyDisablesSnapping) {
                this.setSelectedPoint(evt, this.lastClickedPoint, false);
            } else {
                this.mapService.snappingManagerTool.getSnappingPoint(point).then(
                    (value: any) => this.setSelectedPoint(evt, value || this.lastClickedPoint, true),
                    () => console.debug(`${this.title} - Error finding closest snapping point`)
                );
            }
        }
    }

    private mapDblClickHandler() {
        if (this.mapService.snappingManagerTool) {
            this.enableSnapping();
        }

        if (this.mapService.measurementTool) {
            console.debug(`${this.title} - setupMapDblClickEvent - map click event triggered.`);
            return false;
        }

        return undefined;
    }

    private mapUnloadHandler() {
        if (this.mapDblClickEvent) this.mapDblClickEvent.remove();
        if (this.mapClickEvent) this.mapClickEvent.remove();
        if (this.mapUnloadEvent) this.mapUnloadEvent.remove();

        this.mapClickEvent = undefined;
        this.mapDblClickEvent = undefined;
        this.mapUnloadEvent = undefined;

        this.clear();
    }

    private mapMeasureHandler() {
        const measureGraphic = this.mapService.measurementTool?._measureGraphic;
        if (measureGraphic) {
            console.debug(`${this.title} - on measure event has been triggered`);

            if (!this.inCreateLine) {
                if (this.selectedPoint1 !== undefined && this.selectedPoint2 !== undefined) {
                    this.inCreateLine = true;
                    // Finish line creation feature and start it again
                    this.mapService.disableMeasurementTool();
                    this.mapService.disableSnappingTool();

                    console.debug(`${this.title} - on measure event has been triggered and points have been created and/or found`);

                    const lineGeometry = measureGraphic.geometry;

                    console.debug(`${this.title} - Clicked Point line 1: ${lineGeometry.paths[0][0][0]} / ${lineGeometry.paths[0][0][1]}`);
                    console.debug(`${this.title} - Clicked Point line 2: ${lineGeometry.paths[0][1][0]} / ${lineGeometry.paths[0][1][1]}`);

                    if (this.selectedPoint1) {
                        lineGeometry.paths[0][0][0] = this.selectedPoint1.geometry.x;
                        lineGeometry.paths[0][0][1] = this.selectedPoint1.geometry.y;
                        console.debug(`${this.title} - Found Point 1: ${lineGeometry.paths[0][0][0]} / ${lineGeometry.paths[0][0][1]}`);
                    }

                    if (this.selectedPoint2) {
                        lineGeometry.paths[0][1][0] = this.selectedPoint2.geometry.x;
                        lineGeometry.paths[0][1][1] = this.selectedPoint2.geometry.y;
                        console.debug(`${this.title} - Found Point 2: ${lineGeometry.paths[0][1][0]} / ${lineGeometry.paths[0][1][1]}`);
                    }

                    this.clear();

                    this.createLineFeature(lineGeometry).subscribe(() => this.setupTools());
                }
            }
        }
    }

    private removeSvg() {
        if (this.lineNodeAddEvent.node) {
            if (this.lineNodeAddEvent.node.parentNode) {
                this.lineNodeAddEvent.node.parentNode.removeChild(this.lineNodeAddEvent.node);
            }
            this.lineNodeAddEvent.node = null;
        }
        const lineStyleNode = this.document.getElementById(this.lineStyleNodeId);
        if (lineStyleNode) {
            lineStyleNode.parentNode.removeChild(lineStyleNode);
        }
    }

    private updateSvg(x: number, y: number, offset: boolean, eventName?: string) {
        if (offset) {
            const offsetXY = this.layerUtilService.getMainMapSvgOffsetXY();
            x -= offsetXY[0];
            y -= offsetXY[1];
        }

        const svgNode: SVGSVGElement = this.document.querySelector('#mainMap_gc');
        const svgNodeWidth = svgNode.width.baseVal.value;
        const svgNodeHeight = svgNode.height.baseVal.value;

        if (this.lineNodeAddEvent.node && this.lineNodeAddEvent.graphic.attributes.type !== this.attributes.type.id) {
            this.removeSvg();
            this.lineNodeAddEvent.graphic.attributes.type = this.attributes.type.id;
            this.lineNodeAddEvent.node = this.document.createElementNS('http://www.w3.org/2000/svg', 'path');
        }

        if (this.lineNodeAddEvent.node) {
            // Update line
            if (eventName !== 'zoom-end') {
                const endMapPoint = this.layerUtilService.toMapPoint(x, y);
                this.lineNodeAddEvent.endMapX = endMapPoint.x;
                this.lineNodeAddEvent.endMapY = endMapPoint.y;
            }

            const startScreenPoint = this.layerUtilService.toScreenPoint(this.lineNodeAddEvent.startMapX, this.lineNodeAddEvent.startMapY);
            const endScreenPoint = this.layerUtilService.toScreenPoint(this.lineNodeAddEvent.endMapX, this.lineNodeAddEvent.endMapY);

            let startScreenX = startScreenPoint.x;
            let startScreenY = startScreenPoint.y;
            let endScreenX = endScreenPoint.x;
            let endScreenY = endScreenPoint.y;

            if (eventName === 'mouse-move') {
                startScreenX = this.lineNodeAddEvent.startScreenX;
                startScreenY = this.lineNodeAddEvent.startScreenY;
            } else if (eventName === 'zoom-end') {
                const map = this.spatialUtilService.getMap();
                const mapExtentX = map.extent.xmax - map.extent.xmin;
                const mapExtentY = map.extent.ymax - map.extent.ymin;
                startScreenX = (svgNodeWidth * (this.lineNodeAddEvent.startMapX - map.extent.xmin)) / mapExtentX;
                startScreenY = svgNodeHeight - (svgNodeHeight * (this.lineNodeAddEvent.startMapY - map.extent.ymin)) / mapExtentY;
                endScreenX = (svgNodeWidth * (this.lineNodeAddEvent.endMapX - map.extent.xmin)) / mapExtentX;
                endScreenY = svgNodeHeight - (svgNodeHeight * (this.lineNodeAddEvent.endMapY - map.extent.ymin)) / mapExtentY;
            }

            this.lineNodeAddEvent.startScreenX = startScreenX;
            this.lineNodeAddEvent.startScreenY = startScreenY;

            const pathValue = `M ${startScreenX} ${startScreenY} ${endScreenX} ${endScreenY}`;
            this.lineNodeAddEvent.node.setAttribute('d', pathValue);
            this.lineNodeAddEvent.node.setAttribute('path', pathValue);

            // Display line
            this.layerUtilService.getLayerEventHandler('line', 'graphic-node-add')(this.lineNodeAddEvent);
            this.document
                .getElementById('lineFeatureLayer_layer')
                .insertBefore(this.lineNodeAddEvent.node, this.document.querySelector(`#${this.lineStyleNodeId}`));
        } else {
            // Create line
            this.lineNodeAddEvent.startMapX = this.selectedPoint1.geometry.x;
            this.lineNodeAddEvent.startMapY = this.selectedPoint1.geometry.y;

            this.lineNodeAddEvent.startScreenX = x;
            this.lineNodeAddEvent.startScreenY = y;
            this.lineNodeAddEvent.node = this.document.createElementNS('http://www.w3.org/2000/svg', 'path');
            this.lineNodeAddEvent.graphic.attributes.type = this.attributes.type.id;
        }
    }

    private setSelectedPoint(evt: any, pointGeometry: any, existingPoint: any) {
        const logPrefix = `${this.title} - setSelectedPoint`;
        console.debug(logPrefix);

        if (this.selectedPoint1 === undefined) {
            if (existingPoint) {
                const existingPointFeature = this.getAndSelectExistingPoint(pointGeometry);
                this.selectedPoint1 = existingPointFeature || this.createPointObject(pointGeometry);
                if (existingPointFeature) {
                    const shape = existingPointFeature.getShape().shape;
                    this.updateSvg(shape.cx, shape.cy, false);
                } else {
                    this.updateSvg(evt.layerX, evt.layerY, true);
                }
            } else {
                this.selectedPoint1 = this.createPointObject(pointGeometry);
                this.updateSvg(evt.layerX, evt.layerY, true);
            }
            console.debug(`${logPrefix} - Point 1 selected - x:${this.selectedPoint1.geometry.x} y:${this.selectedPoint1.geometry.y}`);
        } else if (
            this.selectedPoint2 === undefined &&
            !(pointGeometry.x === this.selectedPoint1.geometry.x && pointGeometry.y === this.selectedPoint1.geometry.y)
        ) {
            if (existingPoint) {
                const existingPointFeature = this.getAndSelectExistingPoint(pointGeometry);
                if (existingPointFeature) {
                    this.selectedPoint2 = existingPointFeature;
                } else {
                    this.selectedPoint2 = this.createPointObject(pointGeometry);
                }
            } else {
                this.selectedPoint2 = this.createPointObject(pointGeometry);
            }
            console.debug(`${logPrefix} - Point 2 selected - x:${this.selectedPoint2.geometry.x} y:${this.selectedPoint2.geometry.y}`);
        }
    }

    private createPointObject(geometry: any) {
        console.debug(`${this.title} - createPointObject`);

        const pointGeometry = new this.esriModuleService.geometry.Point();
        pointGeometry.setSpatialReference(geometry.spatialReference);

        pointGeometry.x = geometry.x;
        pointGeometry.y = geometry.y;

        const pointGraphic = new this.esriModuleService.Graphic(pointGeometry);

        pointGraphic.attributes = {};
        pointGraphic.attributes.type = this.attributes.type?.defaultPointTypeId || 0;
        pointGraphic.attributes.visible = 'YES';
        pointGraphic.attributes.x = pointGeometry.x;
        pointGraphic.attributes.y = pointGeometry.y;

        return pointGraphic;
    }

    private getAndSelectExistingPoint(geometry: any) {
        console.debug(`${this.title} - getAndSelectExistingPoint`);

        const features = this.spatialUtilService
            .getPoints()
            .filter((f: any) => f.geometry && this.esriModuleService.geometryEngine.intersects(f.geometry, geometry));

        if (features && features.length === 0) {
            const controlPoints = this.spatialUtilService
                .getControlPoints()
                .filter((f: any) => f.geometry && this.esriModuleService.geometryEngine.intersects(f.geometry, geometry));
            return controlPoints[0];
        } else if (features && features.length > 0) {
            return features[0];
        } else {
            return undefined;
        }
    }

    private createLineFeature(lineGeometry: any) {
        console.debug(`${this.title} - createLineFeature`);

        this.attributes = this.constructionToolModelService.getToolAttrs('line').reduce((acc: any, v: any) => {
            acc[v.prop] = v.value;
            return acc;
        }, {});

        const newPoints = [];
        const existingPoints = [];

        if (this.selectedPoint1.attributes.id) {
            existingPoints.push(this.selectedPoint1);
        } else {
            newPoints.push(this.selectedPoint1);
        }

        if (this.selectedPoint2.attributes.id) {
            existingPoints.push(this.selectedPoint2);
        } else {
            newPoints.push(this.selectedPoint2);
        }

        newPoints.forEach(p => (p.attributes.type = this.attributes.type.defaultPointTypeId));

        return this.createPointFeatures(newPoints).pipe(switchMap(() => this.commitLineFeatureCreation(lineGeometry)));
    }

    private createPointFeatures(points: any[]) {
        console.debug(`${this.title} - createPointFeatures`);

        const controlPointTypeLookups: PointTypeLookup[] = this.lookupService.getLookups('controlPointType');
        const controlPoints = points.filter(p => controlPointTypeLookups.some(l => l.id === p.attributes.type));

        const pointTypeLookups: PointTypeLookup[] = this.lookupService.getLookups('pointType');
        const otherPoints = points.filter(p => pointTypeLookups.some(l => l.id === p.attributes.type));

        const observables = [];

        if (otherPoints.length > 0) {
            const pointSequence = this.spatialSequenceService.getPointSequence(otherPoints.length);
            otherPoints.forEach((p, i) => {
                p.attributes.id = pointSequence[i];
            });

            const pointFeatureLayer = this.spatialUtilService.getPointMapLayer();
            observables.push(
                this.featuresApplyInsertService.insertFeaturesByType(pointFeatureLayer, otherPoints).pipe(
                    map((createdPoints: any[]) => {
                        console.debug('Non Control Points have been successfully created!');
                        return createdPoints.length > 0;
                    })
                )
            );
        }

        if (controlPoints.length > 0) {
            const controlPointSequence = this.spatialSequenceService.getControlPointSequence(controlPoints.length);
            controlPoints.forEach((p, i) => (p.attributes.id = controlPointSequence[i]));

            const controlPointFeatureLayer = this.spatialUtilService.getControlPointMapLayer();
            observables.push(
                this.featuresApplyInsertService.insertFeaturesByType(controlPointFeatureLayer, controlPoints).pipe(
                    map((createdPoints: any[]) => {
                        console.debug('Control Points have been successfully created!');
                        return createdPoints.length > 0;
                    })
                )
            );
        }

        return forkJoinDefaultIfEmpty(observables);
    }

    private commitLineFeatureCreation(lineGeometry: any) {
        console.debug(`${this.title} - commitLineFeatureCreation`);

        const lineAttributes: any = {};

        for (const [attributeKey, attributeValue] of Object.entries(this.attributes) as any[]) {
            if (attributeValue && typeof attributeValue === 'object') {
                lineAttributes[attributeKey] = attributeValue.id;
            } else {
                lineAttributes[attributeKey] = attributeValue;
            }
        }

        lineAttributes.frompointid = this.selectedPoint1.attributes.id;
        lineAttributes.topointid = this.selectedPoint2.attributes.id;

        if (Object.hasOwn(this.attributes, 'bearing') && this.attributes.bearing === undefined) {
            const bearing = this.lineService.calculateBearing(
                lineGeometry.paths[0][0][0],
                lineGeometry.paths[0][0][1],
                lineGeometry.paths[0][1][0],
                lineGeometry.paths[0][1][1]
            );
            lineAttributes.bearing = ddToDms(bearing);
        }

        let distance = 0;
        if (Object.hasOwn(this.attributes, 'distance')) {
            distance = parseFloat(this.attributes.distance);
            if (isNaN(distance)) {
                distance = this.constructionUtilService.getLineGeometryLength(lineGeometry);
            }
        }
        lineAttributes.distance = parseFloat(distance.toFixed(3));

        return this.lineService.commitLineFeatureCreation({geometry: lineGeometry, attributes: lineAttributes});
    }
}
